/**
 * The page someone asked for survives an SSO sign-in (GSL-4).
 *
 * Password sign-in renders the login page in place, so the address bar keeps
 * the URL on its own. SSO leaves for the identity provider and comes back on
 * /auth/callback, which used to land everyone on the home page: a link to
 * /academy/programs/maic-aware/exam lost its destination.
 *
 * The login page parks the current path in sessionStorage right before it
 * leaves for the provider (same tab, survives the round trip, never sent to
 * the provider or any third party), and SSOCallbackPage takes it back.
 * Whatever comes out is validated again: only a path on this origin, never a
 * scheme, another host (`//host`, `/\host`) or the callback itself, so the
 * value cannot become an open redirect. The fragment is never stored. A
 * parked path older than RETURN_TTL_MS is ignored.
 */

const RETURN_KEY = 'nexus_sso_return_to';
export const RETURN_TTL_MS = 15 * 60 * 1000;
const MAX_LENGTH = 2048;

function storage(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** `raw` as a same-origin path plus query, or null when it is not one we
 *  would send someone back to. */
export function safeReturnPath(raw: unknown, origin: string = window.location.origin): string | null {
  if (typeof raw !== 'string' || !raw || raw.length > MAX_LENGTH) return null;
  // A path, not a URL: one leading slash, no backslash (browsers read `/\`
  // as `//`), no control characters (they are stripped before parsing).
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (/[\u0000-\u001F\u007F]/.test(raw)) return null;
  let url: URL;
  try { url = new URL(raw, origin); } catch { return null; }
  if (url.origin !== origin) return null;
  const path = url.pathname;
  if (path === '/' || path === '/index.html' || path === '/auth' || path.startsWith('/auth/')) return null;
  return path + url.search;
}

/** Park where the person is now, before leaving for the identity provider. */
export function parkReturnPath(loc: Pick<Location, 'pathname' | 'search'> = window.location): void {
  const path = safeReturnPath(loc.pathname + loc.search);
  try {
    if (path) storage()?.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
    else storage()?.removeItem(RETURN_KEY);
  } catch { /* storage blocked: the person lands on the home page, as before */ }
}

/** Take (and clear) the parked path, if it is still fresh and safe. */
export function takeReturnPath(now: number = Date.now()): string | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(RETURN_KEY);
    s.removeItem(RETURN_KEY);
    if (!raw) return null;
    const parked = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parked.at !== 'number' || now - parked.at > RETURN_TTL_MS) return null;
    return safeReturnPath(parked.path);
  } catch { return null; }
}
